import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TabsModule } from 'primeng/tabs';
import { Roles } from './shared/roles/roles-list';
import { Users } from './shared/users/users-list';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsActions } from '@tourfold/features/settings-ui';
import {
  readStringParam,
  readTabFromQueryParams,
  syncQueryParams,
  syncTabToUrl,
} from '@tourfold/libraries/shared-util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { RoleTypeFilter } from './shared/roles/roles-list';

const TAB_VALUES = ['users', 'roles'] as const;
const ROLE_TYPE_FILTER_VALUES = ['all', 'system', 'custom'] as const;

@Component({
  selector: 'tf-settings-page-access',
  imports: [TabsModule, Roles, Users, TranslateModule, SettingsActions],
  templateUrl: './settings-access.component.html',
  styleUrl: './settings-access.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Access {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly activeTab = signal(
    readTabFromQueryParams(this.route.snapshot.queryParams, 'tab', TAB_VALUES, 'users')
  );
  readonly roleSearchQuery = signal(
    readStringParam(this.route.snapshot.queryParams, 'roleSearch', '')
  );
  readonly roleTypeFilter = signal(readRoleTypeFilter(this.route.snapshot.queryParams));
  readonly rolesList = viewChild(Roles);
  readonly isRoleEditorActive = computed(
    () => this.activeTab() === 'roles' && (this.rolesList()?.isEditorOpen() ?? false)
  );
  readonly isRoleEditorSaving = computed(() => this.rolesList()?.isSaving() ?? false);
  readonly isRoleEditorSaveDisabled = computed(() => this.rolesList()?.isSaveDisabled() ?? true);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      const tab = readTabFromQueryParams(params, 'tab', TAB_VALUES, 'users');
      this.activeTab.set(tab);
      this.roleSearchQuery.set(readStringParam(params, 'roleSearch', ''));
      this.roleTypeFilter.set(readRoleTypeFilter(params));
    });
  }

  onTabChange(tab: string): void {
    this.activeTab.set(tab);
    syncTabToUrl(this.router, this.route, 'tab', tab);
  }

  onRoleSearchQueryChange(query: string): void {
    this.roleSearchQuery.set(query);
    syncQueryParams(this.router, this.route, {
      roleSearch: query.trim() === '' ? undefined : query,
    });
  }

  onRoleTypeFilterChange(filter: RoleTypeFilter): void {
    this.roleTypeFilter.set(filter);
    syncQueryParams(this.router, this.route, {
      roleType: filter === 'all' ? undefined : filter,
    });
  }
}

function readRoleTypeFilter(queryParams: Record<string, string>): RoleTypeFilter {
  const value = readStringParam(queryParams, 'roleType', 'all');
  return ROLE_TYPE_FILTER_VALUES.includes(value as RoleTypeFilter)
    ? (value as RoleTypeFilter)
    : 'all';
}
